// src/lib/supabase.ts
import { createClient } from "@supabase/supabase-js";
import { Database } from "../types/database.types";
import { toast } from "react-hot-toast";

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || "";
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || "";

if (!supabaseUrl || !supabaseAnonKey) {
  console.error("Missing Supabase environment variables");
}

// Create a single instance of the Supabase client with proper persistence
const supabaseClient = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true, // This is critical for persistence
    autoRefreshToken: true,
    detectSessionInUrl: true,
    storage: localStorage, // Explicitly set to localStorage
    storageKey: "orra-auth-token", // Custom key to avoid conflicts
    flowType: "pkce", // Use PKCE flow for better security
  },
});

// Helper functions
export const supabase = {
  // Auth methods
  async signIn(email: string, password: string) {
    try {
      const { data, error } = await supabaseClient.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      // Force storage of session
      if (data.session) {
        await supabaseClient.auth.setSession(data.session);
      }

      return { data, error: null };
    } catch (error: any) {
      handleError(error, "signIn");
      return { data: null, error };
    }
  },

  async signUp(email: string, password: string, userData?: any) {
    try {
      const { data, error } = await supabaseClient.auth.signUp({
        email,
        password,
        options: {
          data: userData,
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });

      if (error) throw error;

      // Force storage of session if returned
      if (data.session) {
        await supabaseClient.auth.setSession(data.session);
      }

      return { data, error: null };
    } catch (error: any) {
      handleError(error, "signUp");
      return { data: null, error };
    }
  },

  async signOut() {
    try {
      // Clear all storage first
      localStorage.removeItem("orra-auth-token");
      sessionStorage.clear();

      const { error } = await supabaseClient.auth.signOut();
      if (error) throw error;
      return { error: null };
    } catch (error: any) {
      handleError(error, "signOut");
      return { error };
    }
  },

  async getSession() {
    try {
      const {
        data: { session },
        error,
      } = await supabaseClient.auth.getSession();
      if (error) throw error;
      return { data: session, error: null };
    } catch (error: any) {
      return { data: null, error };
    }
  },

  async getProfile(userId?: string) {
    try {
      // Get current user
      const {
        data: { user },
      } = await supabaseClient.auth.getUser();
      if (!user) throw new Error("No authenticated user");

      // Try to get profile by uid
      const { data, error } = await supabaseClient
        .from("profiles")
        .select("*")
        .eq("uid", user.id)
        .single();

      if (error && error.code !== "PGRST116") throw error;

      // If profile doesn't exist, create it
      if (!data) {
        const { data: newProfile, error: createError } = await supabaseClient
          .from("profiles")
          .insert({
            uid: user.id,
            user_id: user.id,
            email: user.email || "",
            full_name: user.user_metadata?.full_name || "",
            profile_setup_completed: false,
            profile_completion: 0,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
          .select()
          .single();

        if (createError) throw createError;
        return { data: newProfile, error: null };
      }

      return { data, error: null };
    } catch (error: any) {
      handleError(error, "getProfile");
      return { data: null, error };
    }
  },

  async updateProfile(profileData: any) {
    try {
      const {
        data: { user },
      } = await supabaseClient.auth.getUser();
      if (!user) throw new Error("No authenticated user");

      const { data, error } = await supabaseClient
        .from("profiles")
        .update({
          ...profileData,
          updated_at: new Date().toISOString(),
        })
        .eq("uid", user.id)
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      handleError(error, "updateProfile");
      return { data: null, error };
    }
  },

  async createCommunityInvite(
    userId: string,
    token: string,
    expiresAt: string,
  ) {
    try {
      const { data, error } = await supabaseClient
        .from("community_invites")
        .insert({
          user_id: userId,
          token,
          expires_at: expiresAt,
          used: false,
        })
        .select()
        .single();

      if (error) throw error;
      return { data, error: null };
    } catch (error: any) {
      handleError(error, "createCommunityInvite");
      return { data: null, error };
    }
  },

  // Get the current user
  async getUser() {
    try {
      const {
        data: { user },
        error,
      } = await supabaseClient.auth.getUser();
      if (error) throw error;
      return { data: user, error: null };
    } catch (error: any) {
      return { data: null, error };
    }
  },

  // Storage methods
  storage: {
    from(bucket: string) {
      return supabaseClient.storage.from(bucket);
    },
  },

  // Get the client for direct access
  getClient() {
    return supabaseClient;
  },
};

// Error handling
function handleError(error: any, context: string) {
  console.error(`Supabase error in ${context}:`, error);

  const errorMessages: Record<string, string> = {
    "User already registered": "This email is already registered.",
    "Invalid login credentials": "Invalid email or password.",
    "Email not confirmed": "Please confirm your email before logging in.",
    "Network error": "Network error. Please check your connection.",
  };

  const userMessage =
    errorMessages[error.message] || error.message || "An error occurred.";
  toast.error(userMessage);
}

export default supabase;
